<template>
  <section class="section lottery-section">
    <div class="section-header">
      <div class="section-title medium">{{ title }}</div>
      <div class="nav-buttons mobile-only">
        <NavButtons @onPrevious="scrollLeft" @onNext="scrollRight" />
      </div>
    </div>
    
    <LotteryFilters
      v-if="showFilters"
      :active-country-slugs="activeCountrySlugs"
      @search="handleSearch"
      @filterCountry="handleCountryFilter"
    />
    
    <div class="lottery-container">
      <button class="scroll-btn scroll-left" @click="scrollLeft">
        <img src="/img/icons/scroll-left.svg" :alt="$t('forms.scrollLeft')" class="scroll-icon" />
      </button>

      <div class="lottery-grid" ref="gridRef">
        <div v-for="lottery in filteredLotteries" :key="`${lottery.game_slug}-${lottery.id}`">
          <transition name="fade" @after-leave="() => finalizeRemove(lottery)">
        <LotteryCard
              v-if="!lottery._removing"
          :lottery="lottery"
          :isFavorited="isFavorited"
          @favorite="() => $emit('favorite', lottery)"
          @bet="() => $emit('bet', lottery)"
              @remove="handleRemove(lottery)"
        />
          </transition>
        </div>
      </div>

      <button class="scroll-btn scroll-right" @click="scrollRight">
        <img src="/img/icons/scroll-right.svg" :alt="$t('forms.scrollRight')" class="scroll-icon" />
      </button>
    </div>
  </section>
</template>

<script lang="ts">
export default {
  name: 'LotterySection'
}
</script>

<script setup lang="ts">
import { ref, defineProps, defineEmits, watch, computed } from 'vue'
import LotteryCard from '@/components/LotteryCard.vue'
import LotteryFilters from '@/components/LotteryFilters.vue'
import NavButtons from './base/NavButtons.vue'
import type { UpcomingDraw } from '@/types/lottery'

const props = defineProps<{
  title: string
  lotteries: UpcomingDraw[]
  isFavorited?: (draw: UpcomingDraw) => boolean
  showFilters?: boolean
}>()

const emit = defineEmits<{
  (e: 'favorite', draw: UpcomingDraw): void
  (e: 'bet', draw: UpcomingDraw): void
  (e: 'removeLottery', draw: UpcomingDraw): void
}>()

// Local copy with _removing property
const localLotteries = ref((props.lotteries || []).map(l => ({ ...l, _removing: false } as UpcomingDraw & { _removing: boolean })));

// Filter states
const searchQuery = ref('');
const selectedCountry = ref('');

const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>();
  for (const draw of props.lotteries || []) {
    if (draw?.country) {
      slugs.add(String(draw.country).toLowerCase());
    }
  }
  return [...slugs];
});

// Filtered lotteries based on search and country filters
const filteredLotteries = computed(() => {
  let filtered = [...localLotteries.value];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(draw => 
      (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
      (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase()))
    );
  }

  // Apply country filter
  if (selectedCountry.value) {
    filtered = filtered.filter(draw => draw.country === selectedCountry.value);
  }

  return filtered;
});

// Filter handlers
const handleSearch = (query: string) => {
  searchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country;
};

watch(() => props.lotteries, (newVal) => {
  localLotteries.value = (newVal || []).map(l => ({ ...l, _removing: false }));
});

const handleRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    localLotteries.value[idx]._removing = true;
  }
};

const finalizeRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    emit('removeLottery', lotteryToRemove);
    localLotteries.value.splice(idx, 1);
  }
};

const gridRef = ref<HTMLElement | null>(null)

const scrollLeft = () => {
  if (gridRef.value) {
    const cardWidth = gridRef.value.offsetWidth / 5;
    gridRef.value.scrollLeft -= cardWidth * 5;
  }
}

const scrollRight = () => {
  if (gridRef.value) {
    const cardWidth = gridRef.value.offsetWidth / 5;
    gridRef.value.scrollLeft += cardWidth * 5;
  }
}
</script>

<style scoped>
.lottery-section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
}

@media (max-width: 768px) {
  .section-header {
    justify-content: space-between;
  }
}


.nav-buttons.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .nav-buttons.mobile-only {
    display: block;
    position: absolute;
    right: 0;
  }
}

@media (max-width: 768px) {
  .nav-buttons {
    display: flex;
  }
  
  .lottery-container .scroll-btn {
    display: none;
  }


  .lottery-grid {
    margin: 0;
    padding: 8px 0;
  }
}

.lottery-container {
  position: relative;
  width: 100%;
  padding: 0;
  margin: 24px 0;
  overflow: hidden;
}

.lottery-grid {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 16px 0;
  white-space: nowrap;
  margin: 0 40px;
}

@media (max-width: 768px) {
  .lottery-grid {
    margin: 0;
    padding: 0;
    gap: 12px;
  }

  .lottery-container {
    margin: 0;
    width: 100%;
  }
}

.lottery-grid > * {
  flex: none;
  min-width: 0;
  background: var(--bg-secondary);
}

.lottery-grid::-webkit-scrollbar {
  display: none;
}

.scroll-btn {
  position: absolute;
  background: none;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;
}

.scroll-btn.scroll-left {
  left: 4px;
}

.scroll-btn.scroll-right {
  right: 4px;
}

.scroll-icon {
  filter: var(--filter-light,none);
}
@media (max-width: 768px) {
  .scroll-btn {
    display: none;
  }
  
  .scroll-icon {
    width: 20px;
    height: 20px;
    /* Light mode: keep original dark color for visibility on light background */
    filter: var(--filter-light,none);
    transition: filter 0.2s ease;
  }
  
  /* Dark mode: make the dark SVG appear white */
  .theme-dark .scroll-icon {
    filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
  }
}


.scroll-btn.scroll-left {
  left: 4px;
}

.scroll-btn.scroll-right {
  right: 4px;
}

.scroll-btn svg {
  width: 20px;
  height: 20px;
  /* Light mode: keep original dark color for visibility on light background */
  filter: none;
  transition: filter 0.2s ease;
}

/* Dark mode: make the dark SVG appear white */
.theme-dark .scroll-icon {
  filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
